<template>
  <div class="kf-plink">
    <div class="kf-plink__head">Partner link</div>
    <div class="kf-plink__help">
      Give every partner this same link. Each one swaps
      <code>{partner_id}</code>, <code>{publisher}</code> and <code>{source}</code>
      for its own values — or build a filled-in link below. Extra params a partner
      adds (e.g. <code>&amp;sub1=…</code>) are kept. Every click is counted, and an
      install from the Play Store is tied back to its click.
    </div>

    <div class="kf-plink__row">
      <v-text-field
        :value="url"
        label="Partner link"
        readonly
        dense
        outlined
        hide-details
        class="kf-plink__url"
        @focus="$event.target.select()"
      />
      <v-btn small depressed color="primary" class="text-none" @click="copy(url)">
        <LucideIcon name="copy" :size="15" class="mr-1" />
        Copy
      </v-btn>
    </div>

    <div class="kf-plink__head kf-plink__head--sub">Build a link for one partner</div>
    <v-row dense>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="partner_id"
          label="Partner ID"
          dense
          outlined
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          v-model="publisher"
          label="Publisher"
          dense
          outlined
          hide-details
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field v-model="source" label="Source" dense outlined hide-details />
      </v-col>
    </v-row>
    <div class="kf-plink__row mt-2">
      <v-text-field
        :value="builtUrl"
        label="Link for this partner"
        readonly
        dense
        outlined
        hide-details
        class="kf-plink__url"
        @focus="$event.target.select()"
      />
      <v-btn
        small
        depressed
        color="primary"
        class="text-none"
        :disabled="!anyFilled"
        @click="copy(builtUrl)"
      >
        <LucideIcon name="copy" :size="15" class="mr-1" />
        Copy
      </v-btn>
    </div>
    <div class="kf-plink__note">
      Letters, numbers, spaces and - _ . @ : + are kept; anything else is dropped
      when the link is clicked.
    </div>
  </div>
</template>

<script>
/**
 * The one dynamic link a campaign is shared under, and a builder for a
 * partner who would rather be handed a filled-in copy.
 *
 * The link itself comes off the server (`dynamic_url` on the campaign row): it
 * carries a signed token and the API's own address, neither of which this
 * component may assemble. The builder only substitutes the three literal
 * placeholders in that server-made string, so the token is never touched.
 */
import { copyToClipboard } from "@/app-js/karrofin/clipboard";

const PLACEHOLDERS = {
  partner_id: "{partner_id}",
  publisher: "{publisher}",
  source: "{source}",
};

export default {
  name: "KfCampaignPartnerLink",
  props: {
    /** The campaign's dynamic_url, placeholders and all. */
    url: { type: String, default: "" },
  },
  data() {
    return {
      partner_id: "",
      publisher: "",
      source: "",
    };
  },
  computed: {
    anyFilled() {
      return !!(this.partner_id || this.publisher || this.source).trim();
    },
    /** A blank field empties its slot rather than leaving "{publisher}" behind. */
    builtUrl() {
      let built = this.url || "";
      Object.keys(PLACEHOLDERS).forEach((key) => {
        const value = (this[key] || "").trim();
        built = built.split(PLACEHOLDERS[key]).join(encodeURIComponent(value));
      });
      return built;
    },
  },
  methods: {
    copy(text) {
      copyToClipboard(text).then((ok) => {
        this.showSnakeBar(
          ok ? "success" : "error",
          ok ? "Partner link copied" : "Could not copy — select the link and copy it"
        );
      });
    },
  },
};
</script>

<style scoped>
.kf-plink {
  margin-top: 18px;
}
.kf-plink__head {
  font-size: var(--kf-fs-label);
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-bottom: 6px;
}
.kf-plink__head--sub {
  margin-top: 16px;
}
.kf-plink__help,
.kf-plink__note {
  font-size: var(--kf-fs-label);
  color: var(--kf-text-muted);
  line-height: 1.5;
  margin-bottom: 10px;
}
.kf-plink__note {
  margin: 6px 0 0;
}
.kf-plink__help code {
  font-size: 11px;
}
.kf-plink__row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.kf-plink__url >>> input {
  font-family: monospace;
  font-size: 12px;
}
</style>
